<script>
import moment from 'moment'
import { fileToBase64 } from '../../../_default/utils/fileToBase64'
import { lotesLeilaoEmMassa } from '../../../../domain/automacoes/services'
import LeilaoService from '../../../../domain/leiloes/services'

const EXTENSOES_PERMITIDAS = ['xlsx', 'xls', 'csv']
const TAMANHO_MAXIMO_BYTES = 5 * 1024 * 1024 // 5MB, mesmo limite validado na API

export default {
  name: 'LotesLeilaoEmMassa',
  data () {
    return {
      leilaoId: null,
      leilao: null,
      buscandoLeilao: false,
      erroLeilao: null,
      arquivo: null,
      arquivoBase64: null,
      loading: false,
      resultado: null // resposta da API (preview ou aplicado)
    }
  },
  computed: {
    temPreview () {
      return !!this.resultado && !this.resultado.aplicado
    },
    temResultadoAplicado () {
      return !!this.resultado && this.resultado.aplicado
    },
    podeConfirmar () {
      return this.temPreview && this.resultado.totalAtualizadas > 0
    }
  },
  methods: {
    extensao (nome) {
      return (nome.split('.').pop() || '').toLowerCase()
    },
    async buscarLeilao () {
      this.leilao = null
      this.erroLeilao = null
      if (!this.leilaoId) return

      this.buscandoLeilao = true
      try {
        const { data } = await LeilaoService.findSimple(this.leilaoId)
        this.leilao = data
      } catch (response) {
        this.erroLeilao = 'Leilão não encontrado.'
      } finally {
        this.buscandoLeilao = false
      }
    },
    trocarLeilao () {
      this.leilaoId = null
      this.leilao = null
      this.erroLeilao = null
      this.trocarArquivo()
    },
    escolherArquivo () {
      if (this.loading) return
      this.$refs.inputArquivo.click()
    },
    async onFileChange (e) {
      const file = e.target.files[0]
      this.limparInput()
      if (!file) return

      if (!EXTENSOES_PERMITIDAS.includes(this.extensao(file.name))) {
        alert('Formato de arquivo inválido. Envie um .xlsx, .xls ou .csv.')
        return
      }
      if (file.size > TAMANHO_MAXIMO_BYTES) {
        alert('Arquivo muito grande. Limite de 5MB.')
        return
      }

      this.resultado = null
      this.arquivo = file
      this.arquivoBase64 = await fileToBase64(file)
    },
    limparInput () {
      if (this.$refs.inputArquivo) this.$refs.inputArquivo.value = ''
    },
    trocarArquivo () {
      this.arquivo = null
      this.arquivoBase64 = null
      this.resultado = null
    },
    async analisar () {
      await this.enviar(false)
    },
    async confirmar () {
      if (!this.podeConfirmar) return
      const confirma = confirm(
        `Confirma a atualização de ${this.resultado.totalAtualizadas} lote(s) do leilão #${this.leilaoId}? Esta ação não pode ser desfeita automaticamente.`
      )
      if (!confirma) return
      await this.enviar(true)
    },
    async enviar (aplicar) {
      if (!this.arquivoBase64 || !this.leilao || this.loading) return
      this.loading = true
      this.$loaderOverlay.open().message(aplicar ? 'Aplicando alterações...' : 'Analisando planilha...')
      try {
        const { data } = await lotesLeilaoEmMassa({
          filename: this.arquivo.name,
          data: this.arquivoBase64,
          leilaoId: this.leilaoId,
          aplicar
        })
        this.resultado = data
        if (aplicar) {
          this.$emit('finishSubmit')
        }
      } catch (response) {
        window.responseError(response)
        console.log(response)
      } finally {
        this.loading = false
        this.$loaderOverlay.hide()
      }
    },
    formataData (data) {
      if (!data) return ''
      // Datas vindas da API às vezes chegam como {date, timezone_type, timezone} (serialização padrão do PHP)
      const valor = typeof data === 'object' && data.date ? data.date : data
      return moment(valor).format('DD/MM/YYYY')
    },
    corStatus (status) {
      if (status === 'ok') return 'text-success'
      if (status === 'erro') return 'text-danger'
      return 'text-muted'
    },
    iconeStatus (status) {
      if (status === 'ok') return 'fa-check'
      if (status === 'erro') return 'fa-times'
      return 'fa-minus'
    }
  }
}
</script>

<template>
  <div class="wrapper-md">
    <div class="alert alert-warning">
      <strong>Atenção:</strong> esta automação é específica para preparar lotes de leilão de
      <strong>veículos de roubo/furto</strong> — ela fixa o estado do veículo como "Conservado", a situação como
      "Recuperável" e escreve "VEÍCULO DE ROUBO E FURTO" na observação/descrição do lote. Não use para outros tipos
      de leilão.
    </div>

    <p class="text-muted">
      Envie uma planilha (.xlsx, .xls ou .csv) para atualizar número, valores e status de vários lotes de um mesmo
      leilão de uma só vez. A planilha deve ter uma linha de cabeçalho com as colunas:
    </p>
    <ul class="text-muted">
      <li><strong>GRV</strong> — obrigatória, número da GRV</li>
      <li><strong>LOTE</strong> (ou "Nº LOTE") — número do lote</li>
      <li><strong>Taxa de Arremate</strong>, <strong>Tabela Fipe</strong>, <strong>Avaliação</strong>,
        <strong>Mínimo</strong>, <strong>Valor Inicial</strong> — valores em R$</li>
      <li><strong>Placa</strong> — opcional</li>
    </ul>
    <p class="text-muted">
      A GRV precisa já ter um lote cadastrado neste leilão — esta automação não cria lotes novos. Limite de 5MB e
      2000 linhas por envio.
    </p>

    <div class="form-group" v-if="!leilao">
      <label>ID do leilão</label>
      <div class="input-group" style="max-width: 300px">
        <input
          type="number"
          class="form-control"
          v-model="leilaoId"
          placeholder="Ex: 123"
          :disabled="buscandoLeilao"
          @keyup.enter="buscarLeilao"
        >
        <span class="input-group-btn">
          <button type="button" class="btn btn-default" @click="buscarLeilao" :disabled="!leilaoId || buscandoLeilao">
            <i class="fa fa-spinner fa-spin" v-if="buscandoLeilao"></i>
            <i class="fa fa-search" v-else></i>
          </button>
        </span>
      </div>
      <p class="text-danger m-t-xs" v-if="erroLeilao">{{ erroLeilao }}</p>
    </div>

    <div class="alert alert-info" v-else>
      Leilão <strong>#{{ leilao.id }}</strong> — {{ leilao.titulo }}
      <span v-if="leilao.dataAbertura"> ({{ formataData(leilao.dataAbertura) }})</span>
      <button type="button" class="btn btn-xs btn-link" @click="trocarLeilao" :disabled="loading">
        (trocar leilão)
      </button>
    </div>

    <template v-if="leilao">
      <div class="m-b" v-if="!arquivo">
        <button type="button" class="btn btn-default" @click="escolherArquivo" :disabled="loading">
          <i class="fa fa-upload m-r-xs"></i> Selecionar planilha
        </button>
        <input
          type="file"
          ref="inputArquivo"
          v-show="false"
          accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
          @change="onFileChange"
        >
      </div>

      <div class="m-b" v-else>
        <span><i class="fa fa-file-excel-o m-r-xs"></i> {{ arquivo.name }}</span>
        <button type="button" class="btn btn-xs btn-link" @click="trocarArquivo" :disabled="loading">
          (trocar arquivo)
        </button>
        <div class="m-t-sm" v-if="!resultado">
          <button type="button" class="btn btn-primary" @click="analisar" :disabled="loading">
            <i class="fa fa-search m-r-xs"></i> Analisar planilha (pré-visualização)
          </button>
        </div>
      </div>
    </template>

    <div v-if="resultado">
      <div class="alert" :class="temResultadoAplicado ? 'alert-success' : 'alert-info'">
        <strong>{{ temResultadoAplicado ? 'Alterações aplicadas.' : 'Pré-visualização (nada foi gravado ainda).' }}</strong>
        <br>
        Total de linhas: {{ resultado.totalLinhas }} —
        <span class="text-success">{{ resultado.totalAtualizadas }} para atualizar</span> —
        <span class="text-danger">{{ resultado.totalErros }} com erro</span>
      </div>

      <div class="table-responsive" style="max-height: 45vh; overflow-y: auto;">
        <table class="table table-hover table-striped table-condensed b-t b-light">
          <thead>
          <tr>
            <th>Linha</th>
            <th>GRV</th>
            <th>Status</th>
            <th>Mensagem</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="linha in resultado.linhas" :key="linha.linha">
            <td>{{ linha.linha }}</td>
            <td>{{ linha.numero }}</td>
            <td :class="corStatus(linha.status)">
              <i class="fa" :class="iconeStatus(linha.status)"></i> {{ linha.status }}
            </td>
            <td>{{ linha.mensagem }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="m-t" v-if="temPreview">
        <button type="button" class="btn btn-success" @click="confirmar" :disabled="!podeConfirmar || loading">
          <i class="fa fa-check m-r-xs"></i> Confirmar e aplicar {{ resultado.totalAtualizadas }} alteração(ões)
        </button>
        <span class="text-muted m-l-sm" v-if="!podeConfirmar">Nenhuma linha válida para aplicar.</span>
      </div>

      <div class="m-t" v-if="temResultadoAplicado">
        <button type="button" class="btn btn-default" @click="$emit('close')">Fechar</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
</style>



// WEBPACK FOOTER //
// LotesLeilaoEmMassa.vue?93478c94